import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { bookingAPI } from '../services/api';
import { bookingRouteRef } from '../utils/bookingRouteRef';
import { openBookingInvoice } from '../utils/bookingInvoice';
import {
  userCanRequestCancellation,
  bookingHasRecordedPayment
} from '../utils/bookingPayment.js';
import { bookingNeedsParticipantCancellationSelection } from '../utils/bookingGroupCancellation.js';
import CancellationParticipantPicker from '../components/CancellationParticipantPicker.jsx';
import { pushDocumentScrollLock, popDocumentScrollLock } from '../utils/documentScrollLock';
import CancellationRefundTimeline from '../components/CancellationRefundTimeline.jsx';
import LoadingIndicator from '../components/LoadingIndicator';
import PayRemainingButton from '../components/PayRemainingButton.jsx';
import {
  isBatchEndBeforeToday,
  resolveCustomerBookingTab
} from '../utils/customerBookingTab.js';
import {
  bookingPackageEndDate,
  bookingPackageStartDate,
  formatBookingPackageDateLabel
} from '../utils/bookingPackageDates.js';
import './Bookings.css';
import './Dashboard.css';

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const MoneyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const CancelRequestIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 8l8 8M16 8l-8 8" />
  </svg>
);

const CANCELLATION_REASONS = [
  { value: 'Academic', label: 'Academic' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Other', label: 'Other' }
];

/** Tab filter: cancelled vs finished event vs still upcoming (paid or unpaid). */
const resolveBookingTab = (booking) => resolveCustomerBookingTab(booking);

const formatInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const tabs = ['upcoming', 'completed', 'cancelled'];

const getInvoiceActionLabel = (bookingStatus, paid, total) => {
  const s = String(bookingStatus || '').trim().toLowerCase();
  if (s === 'confirmed' || s === 'fully_paid' || (total > 0 && paid >= total)) {
    return 'Download Invoice';
  }
  return 'Download Proforma Invoice';
};

const canRequestCancellation = (bookingRow) => {
  if (!bookingRow?.raw?.id || bookingRow.isExpiredUnpaid) return false;
  if (bookingRow.status !== 'upcoming') return false;
  return userCanRequestCancellation(bookingRow.raw);
};

const Bookings = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('Other');
  const [cancelDetails, setCancelDetails] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelAttachment, setCancelAttachment] = useState(null);
  const [cancelParticipantRegIds, setCancelParticipantRegIds] = useState([]);
  const cancelFileInputRef = useRef(null);

  const { data, isLoading } = useQuery(
    ['myBookings'],
    async () => {
      const res = await bookingAPI.getMyBookings();
      return res.data?.data || [];
    }
  );

  const bookings = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return list.map((b) => {
      const trek = b.batch?.trek;
      const slug = trek?.slug;
      const routeRef = encodeURIComponent(bookingRouteRef(b));
      const paid = Number(b.amountPaidAtOffice ?? 0) + Number(b.amountPaidOnline ?? 0);
      const total = Number(b.totalAmount ?? 0);
      const pending = Math.max(0, total - paid);
      const hasPaid = bookingHasRecordedPayment(b);
      const detailsUrl = !hasPaid && (b.status === 'pending' || b.status === 'advance_pending')
        ? `/bookings/confirmed/${routeRef}?payment=office`
        : `/bookings/confirmed/${routeRef}`;
      const tab = resolveBookingTab(b);
      const expiredUnpaid = paid <= 0 && isBatchEndBeforeToday(bookingPackageEndDate(b));
      return {
        id: b.id,
        raw: b,
        slug,
        title: trek?.name || 'Trip',
        imageUrl: trek?.media?.[0]?.url || trek?.brochureUrl,
        status: tab,
        isExpiredUnpaid: expiredUnpaid,
        bookingCode: `#${b.bookingCode}`,
        dateLabel: formatBookingPackageDateLabel(b, formatDate),
        amountLabel: paid > 0 ? formatInr(paid) : '—',
        pendingLabel: pending > 0 ? formatInr(pending) : null,
        certificateUrl: tab === 'completed' ? '#' : null,
        invoiceUrl: '#',
        batchId: b.batchId,
        detailsUrl
      };
    });
  }, [data]);

  const counts = useMemo(
    () => ({
      upcoming: bookings.filter((b) => b.status === 'upcoming').length,
      completed: bookings.filter((b) => b.status === 'completed').length,
      cancelled: bookings.filter((b) => b.status === 'cancelled').length
    }),
    [bookings]
  );

  const filtered = useMemo(() => bookings.filter((b) => b.status === activeTab), [bookings, activeTab]);

  useEffect(() => {
    if (!cancelBooking) return;
    pushDocumentScrollLock();
    return () => popDocumentScrollLock();
  }, [cancelBooking]);

  const openCancelModal = (bookingRow) => {
    if (!canRequestCancellation(bookingRow)) return;
    setCancelBooking(bookingRow);
    setCancelReason('Other');
    setCancelDetails('');
    setCancelAttachment(null);
    setCancelParticipantRegIds([]);
    if (cancelFileInputRef.current) cancelFileInputRef.current.value = '';
    setCancelError(null);
    setCancelSuccess(false);
  };

  const closeCancelModal = () => {
    setCancelBooking(null);
    setCancelError(null);
    setCancelSubmitting(false);
    setCancelAttachment(null);
    setCancelParticipantRegIds([]);
    if (cancelFileInputRef.current) cancelFileInputRef.current.value = '';
  };

  const handleSubmitCancellation = async () => {
    if (!cancelBooking?.raw?.id || cancelSubmitting) return;
    setCancelError(null);
    const trimmed = cancelDetails.trim();
    if (trimmed.length < 10) {
      setCancelError('Please enter at least 10 characters describing your request.');
      return;
    }
    const needsParticipantPick = bookingNeedsParticipantCancellationSelection(cancelBooking.raw);
    if (needsParticipantPick && cancelParticipantRegIds.length === 0) {
      setCancelError('Select at least one participant to cancel.');
      return;
    }
    setCancelSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('bookingId', cancelBooking.raw.id);
      formData.append('title', cancelReason);
      formData.append('details', trimmed);
      if (needsParticipantPick) {
        formData.append('registrationIds', JSON.stringify(cancelParticipantRegIds));
      }
      if (cancelAttachment instanceof File) {
        formData.append('attachment', cancelAttachment);
      }
      await bookingAPI.requestCancellation(formData);
      await queryClient.invalidateQueries(['myBookings']);
      setCancelSuccess(true);
      window.setTimeout(() => {
        closeCancelModal();
      }, 2200);
    } catch (e) {
      setCancelError(e.response?.data?.message || e.message || 'Could not submit request.');
    } finally {
      setCancelSubmitting(false);
    }
  };

  return (
    <div className="bookings-page">
      <div className="bookings-shell">
        <div className="bookings-head">
          <h1 className="bookings-title">My Bookings</h1>
          <Link to="/events" className="bookings-new-btn">+ Book New Adventure</Link>
        </div>

        <div className="bookings-tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`bookings-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} ({counts[tab]})
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="bookings-empty">
            <LoadingIndicator label="Loading your bookings..." />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bookings-empty">
            No {activeTab} bookings yet. Start by booking an adventure.
          </div>
        ) : (
          <div className="bookings-list">
            {filtered.map((booking) => (
              <article
                className={`booking-item${booking.isExpiredUnpaid ? ' booking-item--expired' : ''}`}
                key={booking.id}
              >
                <div className="booking-item-image">
                  <img src={booking.imageUrl || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400'} alt={booking.title} />
                </div>
                <div className="booking-item-content">
                  <div className="booking-item-top">
                    <div>
                      <h3>{booking.title}</h3>
                      <p className="booking-id">{booking.bookingCode}</p>
                    </div>
                    {booking.isExpiredUnpaid ? (
                      <span className="booking-pill expired">EXPIRED</span>
                    ) : (
                      <span className={`booking-pill ${booking.status}`}>{booking.status.toUpperCase()}</span>
                    )}
                  </div>

                  <div className="booking-meta">
                    <div className="booking-meta-box">
                      <CalendarIcon />
                      <div>
                        <small>Date</small>
                        <strong>{booking.dateLabel}</strong>
                      </div>
                    </div>
                    <div className="booking-meta-box">
                      <MoneyIcon />
                      <div>
                        <small>Amount Paid</small>
                        <strong>{booking.amountLabel}</strong>
                      </div>
                    </div>
                    {booking.pendingLabel && (
                      <div className="booking-meta-box">
                        <MoneyIcon />
                        <div>
                          <small>Pending</small>
                          <strong className="booking-pending">{booking.pendingLabel}</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`booking-actions${booking.isExpiredUnpaid ? ' booking-actions--disabled' : ''}`}>
                    {booking.status === 'completed' && !booking.isExpiredUnpaid && (
                      <a href={booking.certificateUrl} className="btn-booking-primary">
                        <DownloadIcon /> Download Certificate
                      </a>
                    )}
                    {booking.isExpiredUnpaid ? (
                      <span className="btn-booking-outline btn-booking--disabled" aria-disabled="true">
                        <DownloadIcon /> Payment not received — documents unavailable
                      </span>
                    ) : (
                      <a
                        href="#"
                        className="btn-booking-outline"
                        onClick={(e) => {
                          e.preventDefault();
                          openBookingInvoice(booking.raw);
                        }}
                      >
                        <DownloadIcon /> {getInvoiceActionLabel(
                          booking.raw?.status,
                          Number(booking.raw?.amountPaidAtOffice ?? 0) + Number(booking.raw?.amountPaidOnline ?? 0),
                          Number(booking.raw?.totalAmount ?? 0)
                        )}
                      </a>
                    )}
                    <PayRemainingButton
                      booking={booking.raw}
                      variant="booking"
                      onPaid={() => queryClient.invalidateQueries(['myBookings'])}
                    />
                    {canRequestCancellation(booking) ? (
                      <button
                        type="button"
                        className="btn-cancel-request btn-cancel-request--booking"
                        onClick={() => openCancelModal(booking)}
                      >
                        <CancelRequestIcon /> Request cancellation
                      </button>
                    ) : null}
                    {booking.raw?.pendingCancellationRequest && booking.status === 'upcoming' ? (
                      <p className="booking-cancel-pending-note">Cancellation request pending review.</p>
                    ) : null}
                    {booking.isExpiredUnpaid ? (
                      <>
                        <span className="booking-view-link booking-view-link--disabled" aria-hidden="true">View Details →</span>
                        {booking.slug && (
                          <span className="booking-view-link booking-view-link--disabled" aria-hidden="true">Event Page →</span>
                        )}
                      </>
                    ) : (
                      <>
                        <Link to={booking.detailsUrl} className="booking-view-link">View Details →</Link>
                        {booking.slug && <Link to={`/events/${booking.slug}?batch=${booking.batchId}`} className="booking-view-link">Event Page →</Link>}
                      </>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {cancelBooking?.raw?.id ? (
        <div
          className="dashboard-cancel-modal-overlay"
          role="presentation"
          onClick={closeCancelModal}
        >
          <div
            className="dashboard-cancel-modal dashboard-cancel-modal--with-policy"
            role="dialog"
            aria-labelledby="bookings-cancel-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="dashboard-cancel-modal-close modal-close-circle" onClick={closeCancelModal} aria-label="Close">
              ×
            </button>
            <h2 id="bookings-cancel-title" className="dashboard-cancel-modal-title">
              Request cancellation
            </h2>
            <p className="dashboard-cancel-modal-trip">
              <strong>Trip:</strong> {cancelBooking.title}
            </p>
            {cancelSuccess ? (
              <>
                <p className="dashboard-cancel-modal-success">
                  Your request has been submitted. Our team will review it and get back to you.
                </p>
                <button type="button" className="btn-cancel-submit" onClick={closeCancelModal}>
                  Close
                </button>
              </>
            ) : (
              <>
                <CancellationRefundTimeline
                  compact
                  departureDate={bookingPackageStartDate(cancelBooking.raw) || cancelBooking.raw?.batch?.startDate}
                  totalAmount={
                    cancelBooking.raw?.totalAmount != null
                      ? Number(cancelBooking.raw.totalAmount)
                      : undefined
                  }
                  refundSlabs={
                    cancelBooking.raw?.refundSlabs ||
                    cancelBooking.raw?.batch?.trek?.refundSlabs ||
                    null
                  }
                />
                {cancelError ? <p className="dashboard-cancel-modal-error">{cancelError}</p> : null}
                {bookingNeedsParticipantCancellationSelection(cancelBooking.raw) ? (
                  <CancellationParticipantPicker
                    booking={cancelBooking.raw}
                    selectedRegistrationIds={cancelParticipantRegIds}
                    onChange={setCancelParticipantRegIds}
                    idPrefix="bookings-cancel-participant"
                  />
                ) : null}
                <label className="dashboard-cancel-label" htmlFor="bookings-cancel-reason">Reason</label>
                <select
                  id="bookings-cancel-reason"
                  className="dashboard-cancel-select"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                >
                  {CANCELLATION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <label className="dashboard-cancel-label" htmlFor="bookings-cancel-details">Description</label>
                <textarea
                  id="bookings-cancel-details"
                  className="dashboard-cancel-textarea"
                  rows={5}
                  maxLength={8000}
                  value={cancelDetails}
                  onChange={(e) => setCancelDetails(e.target.value)}
                  placeholder="Tell us why you need to cancel (policy and refunds apply as per your booking terms)."
                />
                <label className="dashboard-cancel-label" htmlFor="bookings-cancel-attachment">
                  Supporting document <span className="dashboard-cancel-optional">(optional)</span>
                </label>
                <input
                  ref={cancelFileInputRef}
                  id="bookings-cancel-attachment"
                  className="dashboard-cancel-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,application/pdf,image/*"
                  onChange={(e) => setCancelAttachment(e.target.files?.[0] || null)}
                />
                <p className="dashboard-cancel-file-hint">
                  PDF, images, or Word — up to 15 MB. Leave empty if you do not have a document to attach.
                </p>
                {cancelAttachment ? (
                  <p className="dashboard-cancel-file-name">
                    Selected: {cancelAttachment.name}{' '}
                    <button
                      type="button"
                      className="dashboard-cancel-file-clear"
                      onClick={() => {
                        setCancelAttachment(null);
                        if (cancelFileInputRef.current) cancelFileInputRef.current.value = '';
                      }}
                    >
                      Remove
                    </button>
                  </p>
                ) : null}
                <div className="dashboard-cancel-modal-actions">
                  <button type="button" className="btn-cancel-secondary" onClick={closeCancelModal} disabled={cancelSubmitting}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-cancel-submit"
                    onClick={handleSubmitCancellation}
                    disabled={cancelSubmitting}
                  >
                    {cancelSubmitting ? 'Submitting…' : 'Submit request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Bookings;
